import React from 'react';
import { Box, Grid, Typography, Autocomplete, TextField, Button } from '@mui/material';
import AddTaskIcon from '@mui/icons-material/AddTask';
import PlaylistAddCheckOutlinedIcon from '@mui/icons-material/PlaylistAddCheckOutlined';
import { useTheme } from '@emotion/react';
import { tokens } from '../../theme';
import MuiDialog from '../../SystemDesign/MuiDialog';
import SkillSelector from '../../SystemDesign/SkillSelector';
import { SnackBarContext } from '../../Context/SnackBarContextProvider';
import { SecondaryOutlinedButton, DialogSecondaryButton } from '../../SystemDesign/Styles';

import {
  useGetUsersQuery,
  useGetSkillsQuery,
  useBulkCreateEvaluationsMutation,
  useGetRaterTypesQuery,
} from '../../services/SkillsManagement';

const AssignSkillsToEmployees = ({ open, onClose }) => {

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { setOpenSnackBar, setInfoAlert } = React.useContext(SnackBarContext);

  // State for employees (Hybrid: Autocomplete + Infinite Scroll)
  const [page, setPage] = React.useState(1);
  const [rowsPerPage] = React.useState(10);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  // Debounce typing to avoid a request per keystroke
  React.useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch((searchTerm || '').trim()), 400);
    return () => clearTimeout(id);
  }, [searchTerm]);
  const [allUsers, setAllUsers] = React.useState([]);
  // Lock to avoid firing multiple next-page requests from a single scroll
  const loadingNextRef = React.useRef(false);

  const usersArgs = React.useMemo(() => ({
    filters: debouncedSearch && debouncedSearch.length >= 2
      ? JSON.stringify({ "username": debouncedSearch })
      : '',
    sorting: '',
    pagination: JSON.stringify({ currentPage: page, rowsPerPage }),
    type: '',
  }), [page, rowsPerPage, debouncedSearch]);

  const { data: usersData, isFetching: isFetchingUsers } = useGetUsersQuery(usersArgs);

  // Append or reset results when fetching
  React.useEffect(() => {
    if (usersData?.users) {
      if (page === 1) {
        setAllUsers(usersData.users); // reset on new search
      } else {
        setAllUsers((prev) => {
          const map = new Map(prev.map((u) => [u.id, u]));
          usersData.users.forEach((u) => { if (!map.has(u.id)) map.set(u.id, u); });
          return Array.from(map.values());
        }); // append dedup
      }
    }
    // unlock pagination after data arrives
    loadingNextRef.current = false;
  }, [usersData, page]);

  const users = allUsers;

  // Selected employees (multi)
  const [selectedEmployees, setSelectedEmployees] = React.useState([]); // array of user objects

  // Local SkillSelector state (mirrors step 2 from AddEvaluation)
  const [selectedSkills, setSelectedSkills] = React.useState([]);
  const [evaluationLevels, setEvaluationLevels] = React.useState({});
  const [evaluationScores, setEvaluationScores] = React.useState({});
  const [evaluationComments, setEvaluationComments] = React.useState({});

  // Fetch ALL skills (no condition)
  const { data: skillsData, isLoading: isLoadingSkills } = useGetSkillsQuery({
    getAllWithoutPagination: true,
    filters: '',
    sorting: '',
  });

  // Fetch rater types to get the id for title "manager"
  const raterTypesArgs = React.useMemo(() => ({
    filters: '',
    sorting: '',
    pagination: JSON.stringify({ currentPage: 1, rowsPerPage: 50 }),
  }), []);
  const { data: raterTypesData } = useGetRaterTypesQuery(raterTypesArgs, { refetchOnMountOrArgChange: false });
  const managerRaterTypeId = React.useMemo(() => {
    const list = raterTypesData?.data?.raterTypes
      || raterTypesData?.raterTypes
      || raterTypesData?.data
      || raterTypesData
      || [];
    const found = Array.isArray(list)
      ? list.find((rt) => String(rt?.name || '').toLowerCase() === 'manager')
      : null;
    const id = found?.id ?? found?.rater_type_id ?? found?.raterTypeId;
    const asNum = Number(id);
    return Number.isFinite(asNum) ? asNum : 2; // fallback to 2 if not found
  }, [raterTypesData]);

  // Map raw skills to SkillSelector format
  const allSkills = React.useMemo(() => {
    const source = Array.isArray(skillsData)
      ? skillsData
      : Array.isArray(skillsData?.skills)
        ? skillsData.skills
        : Array.isArray(skillsData?.data?.skills)
          ? skillsData.data.skills
          : Array.isArray(skillsData?.data)
            ? skillsData.data
            : [];

    return source.map((s) => {
      const id = s?.skill_id ?? s?.id;
      const code = (s?.skill_code ?? id)?.toString() ?? '';
      const name = s?.name ?? s?.skill ?? s?.skill_name ?? '';
      const description = s?.description || '';
      const discipline = s?.discipline?.name || s?.Discipline?.name || s?.discipline || '';
      return {
        skillCode: code,
        skill: name,
        description,
        discipline,
        awareness_description: s?.awareness_description || '',
        skilled_application_description: s?.skilled_application_description || '',
        management_description: s?.management_description || '',
        id,
        raw: s,
      };
    });
  }, [skillsData]);

  // SkillSelector handlers
  const handleToggleSkill = React.useCallback((code, checked) => {
    setSelectedSkills((prev) => (checked ? Array.from(new Set([...prev, code])) : prev.filter((c) => c !== code)));
    if (!checked) {
      setEvaluationLevels(({ [code]: _omit, ...rest }) => rest);
      setEvaluationScores(({ [code]: _omit, ...rest }) => rest);
      setEvaluationComments(({ [code]: _omit, ...rest }) => rest);
    }
  }, []);

  const handleChangeLevel = React.useCallback((code, level) => {
    setEvaluationLevels((prev) => ({ ...prev, [code]: level }));
  }, []);

  const handleChangeScore = React.useCallback((code, score) => {
    const n = Number(score);
    const clamped = Number.isFinite(n) ? Math.min(10, Math.max(0, n)) : '';
    setEvaluationScores((prev) => ({ ...prev, [code]: clamped }));
  }, []);

  const handleChangeComment = React.useCallback((code, comment) => {
    setEvaluationComments((prev) => ({ ...prev, [code]: comment }));
  }, []);

  const [bulkCreate, { isLoading: isSubmitting }] = useBulkCreateEvaluationsMutation();

  const doSubmit = async () => {

    try {
      if (!selectedEmployees || selectedEmployees.length === 0) {
        setInfoAlert({ severity: 'error', message: 'Please select at least one employee.' });
        setOpenSnackBar(true);
        return;
      }
      if (!selectedSkills || selectedSkills.length === 0) {
        setInfoAlert({ severity: 'error', message: 'Please select at least one skill.' });
        setOpenSnackBar(true);
        return;
      }

      // Build a map from displayed code -> actual backend id
      const codeToId = new Map(allSkills.map((s) => [String(s.skillCode), s.id]));

      // Required fields per Swagger
      const today = new Date();
      const yyyyMmDd = today.toISOString().slice(0, 10);
      const defaultEvaluationType = 'oral';
      const defaultRaterTypeId = managerRaterTypeId;

      const body = selectedEmployees
        .map((emp) => {
          const empId = Number(emp?.id);
          if (!Number.isFinite(empId)) return null;
          const skillsPayload = selectedSkills.map((code) => {
            const backendId = codeToId.get(String(code));
            const skill_id = String(
              backendId != null && backendId !== '' ? backendId : code
            );
            const scoreVal = evaluationScores[code] === '' || evaluationScores[code] == null
              ? 0
              : Number(evaluationScores[code]);
            const levelVal = String(evaluationLevels[code] ?? '');
            return {
              skill_id, // Swagger expects string
              score: scoreVal,
              level: levelVal, // send as string from dropdown
              evaluation_date: yyyyMmDd,
              evaluation_type: defaultEvaluationType,
              rater_type_id: defaultRaterTypeId,
              comments: String(evaluationComments[code] ?? ''),
            };
          });
          return {
            employee_id: empId,
            skills: skillsPayload,
          };
        })
        .filter(Boolean);

      if (!body || body.length === 0) {
        setInfoAlert({ severity: 'error', message: 'No valid employees to process.' });
        setOpenSnackBar(true);
        return;
      }

      const resp = await bulkCreate({ body }).unwrap();
      const created = resp?.data?.createdCount ?? 0;
      const total = resp?.data?.totalProcessed ?? body.length;
      setInfoAlert({
        severity: 'success',
        message: `Skills assigned successfully. Processed ${total}, created ${created}.`,
      });
      setOpenSnackBar(true);
      // Close only if dialog is controlled externally
      onClose && onClose();
    } catch (error) {

      setInfoAlert({
        severity: 'error',
        message: error?.data?.message || 'Failed to assign skills',
      });
      setOpenSnackBar(true);
    }
  };

  const dialogContent = () => (
    <Box>
      <Grid container spacing={2}>
        <Grid item size={{ xs: 12 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Employees
          </Typography>
          <Box sx={{ width: '100%' }}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={users}
              loading={isFetchingUsers}
              getOptionLabel={(u) => String(u?.username ?? u?.email ?? u?.id ?? '')}
              value={selectedEmployees}
              onChange={(_, val) => setSelectedEmployees(val || [])}
              onInputChange={(_, val, reason) => {
                if (reason === "input") {
                  setSearchTerm(val || '');
                  setPage(1); // reset page when searching
                  loadingNextRef.current = false; // reset lock on new search
                }
              }}
              ListboxProps={{
                onScroll: (event) => {
                  const listboxNode = event.currentTarget;
                  if (listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 50) {
                    // advance only once until next data arrives
                    if (!isFetchingUsers && !loadingNextRef.current) {
                      loadingNextRef.current = true;
                      setPage((prev) => prev + 1); // load next page
                    }
                  }
                }
              }}
              disablePortal
              fullWidth
              sx={{
                width: '100%',
                minWidth: '100% !important',
                '& .MuiFormControl-root': { width: '100%' },
                '& .MuiInputBase-root': { width: '100%' },
                '& .MuiOutlinedInput-root': { width: '100%' },
                '& .MuiAutocomplete-inputRoot': { width: '100%' },
              }}
              style={{ width: '100%' }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search employees..."
                  fullWidth
                  sx={{ width: '100%', minWidth: '100% !important' }}
                />
              )}
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {selectedEmployees.length === 0
              ? 'Select one or more employees'
              : 'Showing all available skills.'}
          </Typography>
        </Grid>

        <Grid item size={{ xs: 12 }}>
          {isLoadingSkills ? (
            <Typography variant="body2" color="text.secondary">Loading skills...</Typography>
          ) : (allSkills?.length ? (
            <SkillSelector
              title="Skills to assign"
              skills={allSkills}
              selectedSkills={selectedSkills}
              evaluationLevels={evaluationLevels}
              evaluationScores={evaluationScores}
              evaluationComments={evaluationComments}
              onToggleSkill={handleToggleSkill}
              onChangeLevel={handleChangeLevel}
              onChangeScore={handleChangeScore}
              onChangeComment={handleChangeComment}
              showFileUpload={false}
              maxHeight={360}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">No skills found.</Typography>
          ))}
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <MuiDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      icon={<AddTaskIcon />}
      title="Assign Skills to Employees"
      subtitle="Select employees and skills to assign"
      content={dialogContent}
      actionTitle=" Assign Skills"
      existAction={
        <Button
          onClick={doSubmit}
          sx={DialogSecondaryButton(colors)}
          disabled={isSubmitting}
        >
          Assign Skills
        </Button>
      }
      triggerButton={
        (!open && !onClose) ? (
          <Button
            variant="outlined"
            color="primary"
            startIcon={<PlaylistAddCheckOutlinedIcon />}
          >
            Assign Skill
          </Button>
        ) : undefined
      }
    />
  );
};

export default AssignSkillsToEmployees;